/**
 * /api/orgs/:orgId/connection-impact, /connect-account, /revocation-receipts
 *
 * 0232. The three calls around connecting a person to a Workspace: what it will
 * cost, doing it by e-mail address, and what previous connections took.
 *
 * NOTE ON LANGUAGE: every string in this file is English, including the
 * comments -- scripts/check-ui-english.sh scans apps/chaos-ui/src and does not
 * distinguish a comment from a label, deliberately.
 */
import { apiJson } from './client';

export type ConnectSurface =
  | 'role'
  | 'skill'
  | 'connector'
  | 'permission'
  | 'template'
  | 'approvalRule'
  | 'credential';

export interface ConnectionImpactSurface {
  surface: ConnectSurface;
  /** How many marks connecting somebody would switch off. */
  revoking: number;
  /** How many hand-placed marks survive it. */
  surviving: number;
  writable: boolean;
}

export interface ConnectionImpact {
  orgId: string;
  posture: 'closed' | 'inherit_all';
  solo: boolean;
  memberCount: number;
  willRevoke: boolean;
  totalRevoking: number;
  surfaces: ConnectionImpactSurface[];
  origins: string[];
  /** false = the gateway's SHARE_PROFILES_ENABLED is off; nothing is revoked. */
  enabled: boolean;
}

export interface RevocationReceipt {
  batchId: string;
  trigger: string;
  surface: ConnectSurface;
  origin: string;
  revokedCount: number;
  itemKeys: string[];
  previewedCount: number | null;
  createdAt: string;
}

export const workspaceConnectApi = {
  /**
   * The number the owner must see BEFORE he confirms. Always answers (never a
   * 404), so the dialog can tell "nothing will be revoked" from "I could not
   * ask" -- a dialog that cannot tell those apart shows the permissive one.
   */
  impact(orgId: string): Promise<ConnectionImpact> {
    return apiJson<ConnectionImpact>(`/orgs/${orgId}/connection-impact`);
  },

  /**
   * Connect an existing account by e-mail.
   *
   * ALWAYS resolves to the same body, whether or not the address has a CHAOS
   * account -- the gateway never looks it up. The UI must therefore NOT try to
   * report "found" or "not found": there is nothing to report, and a screen
   * that invented a distinction would put back the enumeration oracle the
   * gateway went out of its way not to build.
   */
  connect(
    orgId: string,
    input: { email: string; role?: string; acknowledgeRevocation?: boolean },
  ): Promise<{ status: string; message: string }> {
    return apiJson(`/orgs/${orgId}/connect-account`, { method: 'POST', json: input });
  },

  receipts(orgId: string): Promise<{ receipts: RevocationReceipt[]; enabled: boolean }> {
    return apiJson(`/orgs/${orgId}/revocation-receipts`);
  },
};

export const workspaceConnectKeys = {
  all: ['workspace-connect'] as const,
  impact: (orgId: string) => [...workspaceConnectKeys.all, 'impact', orgId] as const,
  receipts: (orgId: string) => [...workspaceConnectKeys.all, 'receipts', orgId] as const,
};

/** Human labels. One copy table, shared by every screen that shows an impact. */
export const CONNECT_SURFACE_LABEL: Record<ConnectSurface, string> = {
  role: 'roles',
  skill: 'skills',
  connector: 'connectors',
  permission: 'permissions',
  template: 'templates',
  approvalRule: 'automation rules',
  // A label, not a value. No credential value ever reaches this app.
  // chaos-secret-scan: allow -- the string 'credentials' is a UI label
  credential: 'credentials',
};
